import { JobRecord } from './jobWorkspaceModel';
import { calculationPatch, isAcceptedQuote } from '../../quoteCalculations';

export type WriteError = Error | { message?: string } | null | undefined;

export interface JobOperationsRepository {
  updateShift(shiftId: string, updates: JobRecord, expectedRoster?: JobRecord | null): Promise<void>;
  deleteShift(shiftId: string): Promise<void>;
  updateTask(taskId: string, updates: JobRecord): Promise<void>;
  deleteTask(taskId: string): Promise<void>;
  createTasks(tasks: JobRecord[]): Promise<JobRecord[]>;
  createCasual(casual: { name: string; phone: string }): Promise<JobRecord>;
  updateQuote(quoteId: string, updates: JobRecord): Promise<void>;
  deleteQuote(quoteId: string): Promise<void>;
  updateLead(leadId: string, updates: JobRecord): Promise<void>;
  completeLeadsByEmail(email: string, statuses: string[], updates: JobRecord): Promise<void>;
}

export interface JobOperationsWebhooks {
  sendCrew(payload: JobRecord): Promise<void>;
  sendCasualBriefing(payload: JobRecord): Promise<void>;
}

export class CasualRosterPartialFailure extends Error {
  createdCasual: JobRecord;

  constructor(createdCasual: JobRecord, cause: unknown) {
    super(`Casual staff was saved, but could not be linked to the roster: ${errorMessage(cause)}`);
    this.name = 'CasualRosterPartialFailure';
    this.createdCasual = createdCasual;
  }
}

export class CrewNotificationPartialFailure extends Error {
  roster: JobRecord;

  constructor(roster: JobRecord, cause: unknown) {
    super(`The roster was saved, but the crew notification could not be sent: ${errorMessage(cause)}`);
    this.name = 'CrewNotificationPartialFailure';
    this.roster = roster;
  }
}

export const errorMessage = (error: unknown) =>
  error instanceof Error ? error.message : String((error as { message?: string })?.message || error || 'Unknown error');

const assignmentCycleId = () => globalThis.crypto?.randomUUID?.() ||
  'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, character => {
    const value = Math.floor(Math.random() * 16);
    return (character === 'x' ? value : (value & 0x3) | 0x8).toString(16);
  });

export const crewMemberFromStaff = (
  staff: JobRecord,
  assignmentCycle = assignmentCycleId(),
  assignedAt = new Date().toISOString(),
) => ({
  id: staff.id,
  name: staff.full_name || staff.name || 'Crew Member',
  role: staff.role || 'Staff',
  status: 'PENDING',
  phone: staff.phone || '',
  email: staff.email || '',
  assignment_cycle: assignmentCycle,
  assigned_at: assignedAt,
});

export const buildBroadcastPayload = (
  action: 'NEW_BROADCAST' | 'EXTRA_CREW_PING',
  shift: JobRecord,
  lead: JobRecord,
  crew: JobRecord[],
  requiredCount?: number,
) => ({
  action,
  jobId: shift.id,
  masterLeadId: lead.id,
  eventName: action === 'NEW_BROADCAST' ? shift.shift_title || lead.project_ref || 'Quikpix Event' : shift.shift_title,
  eventDate: action === 'NEW_BROADCAST' ? shift.event_date || lead.event_date || 'TBD' : shift.event_date,
  venue: action === 'NEW_BROADCAST' ? shift.venue_location || lead.venue_location || 'TBD' : shift.venue_location,
  ...(action === 'NEW_BROADCAST' ? { requiredStaff: requiredCount } : {}),
  crew,
  equipmentList: shift.equipment_list || [],
  shiftNotes: shift.shift_notes || '',
  travelArrangements: shift.travel_arrangements || '',
});

export const buildCasualBriefingPayload = (shift: JobRecord, lead: JobRecord, member: JobRecord) => {
  const crew = shift.staff_roster?.crew || [];
  const leadTech = crew.find((person: JobRecord) => !String(person.role || '').toLowerCase().includes('casual') && person.id !== member.id);
  return {
    action: 'DISPATCH_CASUAL',
    job_id: shift.id,
    masterLeadId: lead.id,
    event_name: shift.shift_title,
    event_date: shift.event_date,
    venue: shift.venue_location,
    casual_name: member.name,
    casual_phone: member.phone,
    call_time: member.call_time,
    lead_tech_name: leadTech?.name || 'Quikpix Event Manager',
    lead_tech_phone: leadTech?.phone || '0274 932 338',
    equipment_list: shift.equipment_list || [],
    shift_notes: shift.shift_notes || '',
    travel_arrangements: shift.travel_arrangements || '',
  };
};

export const buildWrapUpDraft = (data: JobRecord) => ({
  gallery_url: data.gallery_url || '',
  gallery_password: data.gallery_password || '',
  analytics_url: data.analytics_url || '',
  kpi_data: {
    hours: data.hours || '',
    photos: data.photos || '',
    faces: data.faces || '',
    women: data.women || '',
    men: data.men || '',
    prints: data.prints || '',
    reach: data.reach || '',
    impressions: data.impressions || '',
  },
});

export const buildGearTriageTask = (lead: JobRecord, crewNotes: string, instruction: string, equipmentManager?: JobRecord) => ({
  title: `🛠️ GEAR TRIAGE: ${lead.project_ref || 'Event'}`,
  lead_id: lead.id,
  assignee_id: equipmentManager?.id || null,
  task_type: 'MANUAL',
  status: 'PENDING',
  due_date: new Date(Date.now() + 86400000).toISOString(),
  description: `🚨 CREW REPORT:\n${crewNotes}\n\n📋 MANAGEMENT INSTRUCTION:\n${instruction}`,
});

export const createJobOperationsController = (
  repository: JobOperationsRepository,
  webhooks: JobOperationsWebhooks,
) => {
  const inFlight = new Set<string>();

  const guarded = async <T>(key: string, operation: () => Promise<T>): Promise<{ ignored: boolean; value?: T }> => {
    if (inFlight.has(key)) return { ignored: true };
    inFlight.add(key);
    try {
      return { ignored: false, value: await operation() };
    } finally {
      inFlight.delete(key);
    }
  };

  return {
    async updateShift(shiftId: string, updates: JobRecord) {
      if (['equipment_list', 'equipment_revision', 'equipment_confirmed_at'].some(key => Object.prototype.hasOwnProperty.call(updates, key))) {
        throw new Error('Use transactional equipment confirmation.');
      }
      return repository.updateShift(shiftId, updates);
    },
    deleteShift: (shiftId: string) => repository.deleteShift(shiftId),
    updateTask: (taskId: string, updates: JobRecord) => repository.updateTask(taskId, updates),
    deleteTask: (taskId: string) => repository.deleteTask(taskId),

    async broadcastShift(shift: JobRecord, lead: JobRecord, staff: JobRecord[], selectedIds: string[], requiredCount: number) {
      return guarded(`broadcast:${shift.id}`, async () => {
        const addedCrew = selectedIds.map(id => crewMemberFromStaff(staff.find(person => person.id === id) || { id }));
        const existingCrew = shift.staff_roster?.crew || [];
        const roster = { required_count: requiredCount, crew: [...existingCrew, ...addedCrew] };
        await repository.updateShift(shift.id, { staff_roster: roster }, shift.staff_roster ?? null);
        try {
          await webhooks.sendCrew(buildBroadcastPayload('NEW_BROADCAST', shift, lead, addedCrew, requiredCount));
        } catch (error) {
          throw new CrewNotificationPartialFailure(roster, error);
        }
        return roster;
      });
    },

    async directPlace(shift: JobRecord, lead: JobRecord, staff: JobRecord) {
      return guarded(`direct:${shift.id}:${staff.id}`, async () => {
        const existingCrew = shift.staff_roster?.crew || [];
        if (existingCrew.some((member: JobRecord) => member.id === staff.id)) return { duplicate: true as const };
        const member = crewMemberFromStaff(staff);
        const roster = { ...shift.staff_roster, crew: [...existingCrew, member] };
        await repository.updateShift(shift.id, { staff_roster: roster }, shift.staff_roster ?? null);
        try {
          await webhooks.sendCrew(buildBroadcastPayload('EXTRA_CREW_PING', shift, lead, [member]));
        } catch (error) {
          throw new CrewNotificationPartialFailure(roster, error);
        }
        return { duplicate: false as const, roster };
      });
    },

    async removeCrew(shift: JobRecord, memberId: string) {
      const roster = { ...shift.staff_roster, crew: (shift.staff_roster?.crew || []).filter((member: JobRecord) => member.id !== memberId) };
        await repository.updateShift(shift.id, { staff_roster: roster }, shift.staff_roster ?? null);
      return roster;
    },

    async updateCrewRoster(shift: JobRecord, roster: JobRecord) {
        await repository.updateShift(shift.id, { staff_roster: roster }, shift.staff_roster ?? null);
      return roster;
    },

    async createGearTriage(lead: JobRecord, crewNotes: string, instruction: string, equipmentManager?: JobRecord) {
      const [task] = await repository.createTasks([buildGearTriageTask(lead, crewNotes, instruction, equipmentManager)]);
      return task;
    },

    saveWrapUpDraft(quoteId: string, data: JobRecord) {
      return repository.updateQuote(quoteId, buildWrapUpDraft(data));
    },

    async silentApprove(quote: JobRecord, activeContact: JobRecord, approve?: (quote: JobRecord, notify: boolean) => Promise<void>) {
      if (isAcceptedQuote(quote)) return;
      const calculated = calculationPatch(quote);
      if (approve) await approve({ ...quote, ...calculated, status: 'APPROVED' }, false);
      else await repository.updateQuote(quote.id, { ...calculated, status: 'APPROVED' });
      const leadId = quote.linkedCrmItemId || quote.lead_id || quote.leadId || activeContact?.id;
      if (leadId) await repository.updateLead(leadId, { status: 'STAGING' });
      return leadId;
    },

    deleteQuote: (quoteId: string) => repository.deleteQuote(quoteId),

    async addCasual(shift: JobRecord, casual: JobRecord, create: boolean) {
      let saved = casual;
      if (create) saved = await repository.createCasual({ name: casual.name, phone: casual.phone });
      const member = { id: saved.id, name: `${saved.name} (Casual)`, role: 'Assistant / Casual', status: 'CONFIRMED', phone: saved.phone, email: '', call_time: '' };
      const existingCrew = shift.staff_roster?.crew || [];
      const roster = { required_count: shift.staff_roster?.required_count || existingCrew.length + 1, crew: [...existingCrew, member] };
      try {
        await repository.updateShift(shift.id, { staff_roster: roster }, shift.staff_roster ?? null);
      } catch (error) {
        if (create) throw new CasualRosterPartialFailure(saved, error);
        throw error;
      }
      return { savedCasual: saved, roster };
    },

    dispatchCasual(shift: JobRecord, lead: JobRecord, member: JobRecord) {
      return guarded(`casual-briefing:${shift.id}:${member.id}`, async () => {
        const payload = buildCasualBriefingPayload(shift, lead, member);
        await webhooks.sendCasualBriefing(payload);
        return payload;
      });
    },
  };
};

export type JobOperationsController = ReturnType<typeof createJobOperationsController>;
